<template>
  <footer class="amseu-footer" aria-label="Fußbereich">
    <b-container class="amseu-footer__inner">
      <!-- Social -->
      <div class="amseu-footer__social-block">
        <h2 class="amseu-footer__social-title">Folgen Sie uns auf Social Media</h2>
        <div class="amseu-footer__social" aria-label="Social Media">
          <a href="https://www.linkedin.com/company/ams-arbeitsmarktservice/" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="LinkedIn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
              <rect width="4" height="12" x="2" y="9" />
              <circle cx="4" cy="4" r="2" />
            </svg>
          </a>
          <a href="https://x.com/home?status=https://www.ams.at/arbeitsuchende" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="X (Twitter)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
            </svg>
          </a>
          <a href="https://www.facebook.com/sharer/sharer.php?u=https://www.ams.at/arbeitsuchende" target="_blank" rel="noopener" class="amseu-footer__social-link" aria-label="Facebook">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </a>
        </div>
      </div>

      <div class="amseu-footer__divider"></div>

      <!-- Link columns -->
      <nav class="amseu-footer__nav" aria-label="Footer Navigation">
        <b-row>
          <b-col cols="12" sm="6" lg="3" class="amseu-footer__col" v-for="(col, index) in footerColumns" :key="`col-${index}`">
            <h3 class="amseu-footer__col-title">{{ col.title }}</h3>
            <ul class="amseu-footer__list list-unstyled">
              <li v-for="(link, i) in col.links" :key="`link-${index}-${i}`">
                <a :href="link.link" target="_blank" rel="noopener" class="amseu-footer__link">{{ link.text }}</a>
              </li>
            </ul>
            <div v-if="col.hotline" class="amseu-footer__hotline">
              <p class="amseu-footer__hotline-label">AMS Hotline</p>
              <a href="tel:+4305748580" class="amseu-footer__hotline-phone">0574 48 0</a>
              <p class="amseu-footer__hotline-hours">Mo – Fr, 08:00 – 20:00 Uhr</p>
            </div>
          </b-col>
        </b-row>
      </nav>

      <div class="amseu-footer__divider"></div>

      <!-- Bottom -->
      <div class="amseu-footer__bottom">
        <p class="amseu-footer__copyright">
          © {{ new Date().getFullYear() }} Arbeitsmarktservice Österreich (AMS) · Treustraße 35–43 · 1200 Wien · DVR: 0816672
        </p>
        <div class="amseu-footer__legal">
          <a
            v-for="(link, index) in legalLinks"
            :key="`legal-${index}`"
            :href="link.link"
            :target="link.modalId ? undefined : '_blank'"
            rel="noopener"
            class="amseu-footer__legal-link"
            @click="onLegalClick($event, link)"
          >{{ link.text }}</a>
        </div>
      </div>
    </b-container>

    <b-modal id="datenschutz-modal" title="Datenschutz" size="xl" scrollable ok-only ok-title="Schließen" hide-header-close>
      <div class="amseu-footer__datenschutz" v-html="datenschutzText"></div>
    </b-modal>
  </footer>
</template>

<script lang="ts">
import { AppModule } from '@/store/modules/app';
import { Component, Vue } from 'vue-property-decorator';

interface FooterLink {
  text: string
  link: string
  modalId?: string
}

interface FooterColumn {
  title: string
  links: FooterLink[]
  hotline?: boolean
}

const AMS = 'https://www.ams.at';

@Component
export default class FooterComponentAmseu extends Vue {
  get lang() {
    return AppModule.language;
  }

  footerColumns: FooterColumn[] = [
    {
      title: 'Stellensuche',
      links: [
        { text: 'Aktuelle Stellenangebote', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Erweiterte Suche', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Jobs nach Bundesland', link: `${AMS}/arbeitsuchende` },
        { text: 'Vollzeitstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Teilzeitstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Praktikumsstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Lehrstellen', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/jobsuche-online-und-mobil` },
        { text: 'Jobs in Wien', link: `${AMS}/arbeitsuchende#wien` },
      ],
    },
    {
      title: 'Für Arbeitgeber:innen',
      links: [
        { text: 'Stelle ausschreiben', link: `${AMS}/` },
        { text: 'Fachkräftesuche', link: `${AMS}/` },
        { text: 'eAMS-Konto', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/meinams-alle-infos` },
        { text: 'Förderungen & Beihilfen', link: `${AMS}/` },
        { text: 'Kurzarbeit', link: `${AMS}/` },
        { text: 'Betriebsberatung', link: `${AMS}/` },
      ],
    },
    {
      title: 'Für Arbeitnehmer:innen',
      links: [
        { text: 'Mein AMS-Konto', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/meinams-alle-infos` },
        { text: 'Arbeitslosengeld', link: `${AMS}/arbeitsuchende/arbeitslos-was-tun/beim-ams-arbeitslosengeld-beantragen` },
        { text: 'Notstandshilfe', link: `${AMS}/arbeitsuchende` },
        { text: 'Weiterbildung & Kurse', link: `${AMS}/arbeitsuchende` },
        { text: 'Berufsberatung', link: `${AMS}/arbeitsuchende` },
        { text: 'Qualifikationscheck', link: `${AMS}/arbeitsuchende` },
        { text: 'Berufskompass', link: `${AMS}/arbeitsuchende` },
        { text: 'Bewerbungstipps', link: `${AMS}/arbeitsuchende` },
      ],
    },
    {
      title: 'Über das AMS',
      links: [
        { text: 'Organisation & Aufgaben', link: `${AMS}/organisation` },
        { text: 'Geschäftsberichte', link: `${AMS}/organisation` },
        { text: 'Statistiken & Daten', link: `${AMS}/arbeitsmarktdaten-und-medien/arbeitsmarkt-daten-und-arbeitsmarkt-forschung/unser-statistik-team` },
        { text: 'Presse & Medien', link: `${AMS}/arbeitsmarktdaten-und-medien/medien` },
        { text: 'Karriere beim AMS', link: `${AMS}/organisation` },
        { text: 'Kontakt & Standorte', link: `${AMS}/organisation/kontaktmoeglichkeiten` },
      ],
      hotline: true,
    },
  ];

  legalLinks: FooterLink[] = [
    { text: 'Impressum', link: `${AMS}/organisation/ueber-ams/impressum` },
    { text: 'Datenschutz', link: `${AMS}/organisation/ueber-ams/datenschutzbestimmungen`, modalId: 'datenschutz-modal' },
    { text: 'Barrierefreiheit', link: `${AMS}/organisation/ueber-ams/barrierefreiheitserklaerung/barrierefreiheitserklaerung-ams-webseite` },
    { text: 'Sitemap', link: `${AMS}/sitemap` },
    { text: 'Nutzungsbedingungen', link: `${AMS}/organisation/ueber-ams/allgemeine-geschaeftsbedingungen` },
  ];

  onLegalClick(event: Event, link: FooterLink) {
    if (link.modalId) {
      event.preventDefault();
      this.$bvModal.show(link.modalId);
    }
  }

  datenschutzText = `<p><strong>INFORMATION ZUR DATENSCHUTZ-GRUNDVERORDNUNG – DSGVO</strong><br>
Gemäß Artikel 13 und 14 der Verordnung (EU) 2016/679 (DSVGO) informieren wir Sie über die Datenverarbeitung im Arbeitsmarktservice (AMS) / Dienstleistung EURES<br>
<br>
<strong>Allgemeine Informationen</strong><br>
<br>
Diese Datenschutzerklärung informiert Sie über Art, Umfang und Zweck der Verarbeitung personenbezogener Daten im Rahmen der Nutzung der Webanwendung AMS EURES CRM-System.<br>
Das System dient der Unterstützung der internationalen Fachkräftevermittlung und stellt ein zentrales CRM-System zur Verwaltung von Bewerber:innen, Unternehmen und Stellenangeboten dar.<br>
<br>
<strong>Verantwortlicher</strong><br>
<br>
Arbeitsmarktservice Österreich (AMS)<br>
Dienstleistungsunternehmen des öffentlichen Rechts<br>
Treustraße 35-43<br>
1200 Wien<br>
Telefon: +43 5 09 04 199<br>
E-Mail: ams.oesterreich@ams.at<br>
<br>
<strong>Datenschutzbeauftragter</strong><br>
<br>
Arbeitsmarktservice – Bundesgeschäftsstelle<br>
z.H. Datenschutzorganisation<br>
Treustraße 35-43<br>
1200 Wien<br>
E-Mail: ams.datenschutz@ams.at<br>
<br>
<strong>Auftragsverarbeiter</strong><br>
<br>
Jobnet.AG<br>
Kolonnenstraße 8<br>
10827 Berlin, Deutschland<br>
E-Mail: <a href="mailto:info@jobnet.solutions">info@jobnet.solutions</a><br>
Die Jobnet.AG betreibt das System als Software-as-a-Service (SaaS) und stellt die technische Infrastruktur bereit.<br>
Das Hosting erfolgt in Rechenzentren von Amazon Web Services (AWS) in Frankfurt am Main (Deutschland).<br>
<br>
<strong>Verarbeitungszwecke</strong><br>
<br>
Die Verarbeitung personenbezogener Daten erfolgt zu folgenden Zwecken:<br>
• Durchführung von Rekrutierungs- und Vermittlungsaktivitäten im EU-/EWR-Raum sowie in der Schweiz<br>
• Betrieb eines zentralen CRM-Systems für Bewerber:innen und Unternehmen<br>
• Verwaltung von Stellenangeboten<br>
• Kommunikation zwischen Bewerber:innen, Unternehmen und Berater:innen<br>
• Versand von Stellenangeboten und Vermittlungsvorschlägen<br>
• Dokumentation von Kommunikationsvorgängen<br>
• Durchführung von Matching- und Suchprozessen<br>
• Erstellung von Auswertungen, Statistiken und Monitoring<br>
• Sicherstellung des technischen Betriebs und der IT-Sicherheit<br>
• Datenaustausch mit europäischen und nationalen Partnerorganisationen (insbesondere EURES-Portal der Europäischen Kommission in Brüssel sowie Austrian Business Agency – ABA)<br>
<br>
Das System dient insbesondere der Zentralisierung und Standardisierung von Recruitingprozessen.<br>
<br>
<strong>Kategorien verarbeiteter Daten</strong><br>
<br>
Je nach Nutzung werden folgende personenbezogene Daten verarbeitet:<br>
<br>
▸ Bewerber:innendaten<br>
• Stammdaten (Name, Kontaktdaten, Herkunftsland etc.)<br>
• Qualifikationen, Sprachkenntnisse, Berufserfahrung<br>
• Bewerbungsunterlagen (Lebenslauf, Zertifikate etc.)<br>
• Kommunikationsdaten<br>
• Nutzungs- und Aktivitätsdaten<br>
<br>
▸ Unternehmensdaten<br>
• Unternehmensstammdaten<br>
• Ansprechpartner:innen<br>
• Stellenangebote<br>
<br>
▸ Technische Daten<br>
• IP-Adresse<br>
• Logfiles (Zugriffsdaten)<br>
• Browser- und Gerätedaten<br>
<br>
<strong>Datenquellen</strong><br>
<br>
Die Daten stammen aus folgenden Quellen:<br>
• Eigeneingabe durch Bewerber:innen (z. B. Registrierung via QR-Code oder Formular)<br>
• Import aus externen Systemen (z. B. EURES-Schnittstellen)<br>
• Manuelle Eingabe durch Berater:innen<br>
• Übermittlung durch Partnerorganisationen<br>
<br>
<strong>Empfänger von Daten</strong><br>
<br>
Personenbezogene Daten und Unternehmensdaten werden ausschließlich im notwendigen Umfang weitergegeben:<br>
• interne Nutzer:innen (z. B. EURES-Berater:innen)<br>
• autorisierte externe Partner (z. B. Welcome Center, ABA Work in Austria)<br>
• technische Dienstleister (Auftragsverarbeiter)<br>
• EURES-Portal der Europäischen Kommission (Brüssel):<br>
▸ Übermittlung personenbezogener Daten von Bewerber:innen (z. B. Qualifikationen, berufliche Angaben)<br>
▸ Übermittlung von Unternehmensdaten sowie Stellenangeboten<br>
• Austrian Business Agency (ABA):<br>
▸ Übermittlung personenbezogener Daten (z. B. Bewerber:innen- oder Ansprechpartnerdaten)<br>
▸ Übermittlung von Unternehmensdaten sowie Stellenangeboten<br>
<br>
Eine Weitergabe erfolgt nur im Rahmen der gesetzlichen Bestimmungen oder vertraglicher Vereinbarungen.<br>
<br>
<strong>Rechtsgrundlagen der Verarbeitung</strong><br>
<br>
Die Verarbeitung erfolgt auf Grundlage von:<br>
• Art. 6 Abs. 1 lit. e DSGVO (Wahrnehmung öffentlicher Aufgaben)<br>
• Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse, insbesondere Systembetrieb und Optimierung)<br>
• Art. 6 Abs. 1 lit. a DSGVO (Einwilligung, z. B. bei Bewerber:innenregistrierung und optionalen Funktionen)<br>
• Art. 6 Abs. 1 lit. c DSGVO (Erfüllung einer rechtlichen Verpflichtung, der der Verantwortliche unterliegt)<br>
<br>
<strong>Speicherung von Zugriffsdaten</strong><br>
<br>
Bei jeder Anforderung einer Datei werden folgende Daten gespeichert:<br>
-dem Namen der Datei<br>
-dem Datum und der Uhrzeit der Anforderung<br>
-der übertragenen Datenmenge<br>
-dem Zugriffsstatus (Datei übertragen, Datei nicht gefunden etc.)<br>
-dem Browsertyp<br>
-der IP-Adresse des anfragenden Rechners<br>
<br>
Diese Daten werden ausschließlich zu statistischen Zwecken ausgewertet.<br>
<br>
<strong>Speicherung der IP-Adresse</strong><br>
<br>
Die IP-Adresse wird für maximal sieben Tage gespeichert, um Angriffe erkennen und abwehren zu können. Anschließend wird sie gelöscht.<br>
<br>
<strong>Speicherung von eingegebenen Daten</strong><br>
<br>
Eingegebene Daten werden im Rahmen des CRM-Systems gespeichert und verarbeitet, um:<br>
• Vermittlungsprozesse durchzuführen<br>
• Bewerber:innen mit Stellenangeboten zu matchen<br>
• Kommunikation zu ermöglichen<br>
<br>
<strong>Speicherdauer</strong><br>
<br>
Personenbezogene Daten werden nur so lange gespeichert, wie dies für die jeweiligen Zwecke erforderlich ist.<br>
Insbesondere gilt:<br>
• Bewerberdaten: für die Dauer des Vermittlungsprozesses sowie darüber hinaus gemäß gesetzlichen Aufbewahrungspflichten<br>
• Kommunikationsdaten: entsprechend Dokumentationspflichten<br>
• Technische Daten: kurzfristig (siehe IP-Adresse)<br>
<br>
<strong>Cookies</strong><br>
<br>
Es werden ausschließlich technisch notwendige Session-Cookies verwendet.<br>
Diese dienen der Steuerung der Sitzung und werden nach Beendigung automatisch gelöscht.<br>
Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO<br>
<br>
<strong>Datensicherheit</strong><br>
<br>
Es werden geeignete technische und organisatorische Maßnahmen getroffen, um Daten vor Verlust, Manipulation und unbefugtem Zugriff zu schützen.<br>
<br>
<strong>Google Maps</strong><br>
<br>
Wir nutzen Google Maps zur Darstellung von Karten und zur Erstellung der interaktiven Karte. Google Maps wird von der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4 betrieben.<br>
<br>
Sofern Sie die Karte von Google Maps nutzen, z.B. durch Anklicken unserer dort aufgeführten Standorte, erfassen wir von Ihnen keinerlei Daten. Durch die Nutzung von Google Maps können nach unserer Kenntnis jedoch Informationen über Ihre Benutzung (insbesondere die IP-Adresse Ihres Rechners) an einen Server der Google Ireland Limited in den USA übertragen und dort gespeichert werden. Die Verarbeitung personenbezogener Daten unter Verwendung technisch notwendiger Cookies und Ihrer IP Adresse basiert auf der Rechtsgrundlage von Art. 6 Abs. 1 S. 1 lit. a) DSGVO - Einwilligung. Auf die Weiterverarbeitung von Daten durch die Google Limited Ireland haben wir keinen Einfluss.<br>
<br>
Beim Aufruf der Website wird Ihnen ein Cookie-Banner angezeigt, bei dem Sie entscheiden können, ob Sie die Google-Karte nutzen können. Falls Sie das nicht möchten, dann wird Ihnen die Karte nicht angezeigt. Falls Sie die Zustimmung zurücknehmen möchten, dann können Sie den Cookie-Banner im unteren Bereich der Webseite unter dem Link "Cookie-Richtlinie" erneut aufrufen und die Einwilligung zurücknehmen.<br>
<br>
Bitte lesen Sie auch die Nutzungsbestimmungen von Google Maps, sofern Sie den Service nutzen möchten. Die Nutzungsbedingungen für Google Maps finden Sie unter: http://www.google.com/intl/de_de/help/terms_maps.html.<br>
<br>
Ausführliche Informationen zu den Datenschutzbestimmungen von Google finden Sie zudem unter: http://www.google.de/intl/de/policies/privacy/.<br>
<br>
<strong>KI-gestützte Stellensuche</strong><br>
<br>
Im System wird eine KI-gestützte Suchfunktion eingesetzt, um passende Stellenangebote zu ermitteln. Dabei werden Suchanfragen und Nutzungsdaten verarbeitet.<br>
• keine automatisierten Entscheidungen im Sinne von Art. 22 DSGVO<br>
• Verarbeitung innerhalb eigener Infrastruktur (AWS Frankfurt)<br>
• keine Weitergabe an Dritte<br>
<br>
<strong>Profiling und Matching</strong><br>
<br>
Zur Vermittlung geeigneter Stellenangebote werden Bewerberdaten analysiert und mit Stellenangeboten abgeglichen.<br>
Dies erfolgt ausschließlich zur Unterstützung der Vermittlung und hat keine rechtlichen Auswirkungen im Sinne von Art. 22 DSGVO.<br>
<br>
<strong>Ihre Rechte</strong><br>
<br>
Sie haben folgende Rechte:<br>
• Auskunft (Art. 15 DSGVO)<br>
• Berichtigung (Art. 16 DSGVO)<br>
• Löschung (Art. 17 DSGVO)<br>
• Einschränkung (Art. 18 DSGVO)<br>
• Datenübertragbarkeit (Art. 20 DSGVO)<br>
• Widerspruch (Art. 21 DSGVO)<br>
• Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO)<br>
<br>
<strong>Weitere Informationen finden Sie unter:</strong><br>
<a href="https://www.ams.at/organisation/ueber-ams/datenschutzbestimmungen" target="_blank" rel="noopener">https://www.ams.at/organisation/ueber-ams/datenschutzbestimmungen</a><br>
<br>
<a href="https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&amp;Gesetzesnummer=10001597" target="_blank" rel="noopener"><strong>www.ams.at</strong></a> <strong>Ihr ArbeitsmarktService</strong></p>`;
}
</script>

<style lang="scss" scoped>
$footer-bg: #00478f;

.amseu-footer {
  position: relative;
  z-index: 20;
  margin-top: 4rem;
  background: $footer-bg;
  font-family: Roboto, Arial, sans-serif;

  &__inner {
    max-width: 1400px;
    padding-top: 3rem;
    padding-bottom: 3rem;
  }

  // Social
  &__social-block {
    margin-bottom: 2.5rem;
  }

  &__social-title {
    color: rgba(255, 255, 255, 0.9) !important;
    font-size: 14px;
    font-weight: 400 !important;
    margin-bottom: 1rem;
  }

  &__social {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: #ffffff;
    color: $footer-bg;
    transition: background-color 0.2s ease-in-out;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
  }

  // Divider
  &__divider {
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 2rem;
  }

  // Columns
  &__nav {
    margin-bottom: 0;
  }

  &__col {
    margin-bottom: 2rem;
  }

  &__col-title {
    color: #ffffff !important;
    font-size: 13px;
    font-weight: 700 !important;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 1rem;
  }

  &__list {
    margin: 0;

    li + li {
      margin-top: 0.625rem;
    }
  }

  &__link {
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #ffffff !important;
      text-decoration: underline !important;
    }
  }

  // Hotline
  &__hotline {
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  &__hotline-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  &__hotline-phone {
    color: #ffffff;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__hotline-hours {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }

  // Bottom
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media (min-width: 576px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__copyright {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    margin: 0;
  }

  &__legal {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem 1.25rem;
  }

  &__legal-link {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #ffffff !important;
      text-decoration: underline !important;
    }
  }
}
</style>

<style lang="scss">
.amseu-footer__datenschutz {
  font-family: Roboto, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333333;

  strong {
    color: #00478f;
  }

  a {
    color: #0054A6;
    word-break: break-word;
  }
}
</style>
